// @ts-nocheck: converted from JS

import { DateEditorWrapper } from '@/components/UI/SpreadSheet/editors/DateEditorWrapper';
import type { CustomColumnSettings, SpreadsheetSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import { requiredAsteriskColumnHeader } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import { _isBlank, _isEmptyString, _isNotEmpty, _notNil } from '@/littledash';
import { AnimalSexMap, type AnimalSex } from '@/model/Animal.model';
import type { Animal, AnimalSpecies, AnimalStrain } from '@/model/Animal.model';
import { getSexDropdownSource, createSexValidator } from '@/model/Animal.utils';
import type { Cage } from '@/model/Cage.model';
import type { ID } from '@/model/Common.model';
import type {
  MetadataField,
  MetadataFieldValueTypes,
  MetadataFieldWithValue,
  MetadataGlossaryType,
} from '@/model/Metadata.model';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import { MAX_CAGE_POPULATION } from '@/constants/cage';
import { ApiService } from '@/support/ApiService';
import { defaultMetadataToColumnSettingMapper, formatColumnSettingsDataValue } from '@/support/hot';
import { DateInputUtils } from '@/utils/Date.utils';
import type { EntityFieldRequiredMap } from '@/utils/entitySchema/useEntitySchema';
import { validateDob } from '@/components/Studies/Create/Steps/Animals/Animals.utils.tsx';
import type Handsontable from 'handsontable';

export const animalCountValidater = (value: number): boolean =>
  value <= MAX_CAGE_POPULATION && !isNaN(value) && value !== null && value !== '' && value !== undefined;

export const getMetaData = (filterType: MetadataGlossaryType) =>
  ApiService.call({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      filter_type: filterType,
    },
    options: {
      onError: {
        slug: 'colony-metadata-fetch',
        toast: false,
        capture: true,
        throw: false,
      },
    },
  });

export const fetchExactCageData = (id: ID) =>
  Http.get(
    apiRoute('cage.index', {
      id,
      query: 'counts',
    })
  );

export const getMetaColumns = (selectedRows: Array<Animal | Cage> | undefined = [], metadata: Array<MetadataField>) => {
  const glossaryIDs = selectedRows?.reduce((ids, row) => {
    if (_notNil(row.meta?.data) && _isNotEmpty(row.meta.data)) {
      const metas = row.meta.data.filter((m) => !ids.includes(m.glossary_id)).map((m) => m.glossary_id);
      ids = [...ids, ...metas];
    } else if (_notNil(row.metadata) && _isNotEmpty(row.metadata)) {
      const metas = row.metadata.filter((m) => !ids.includes(m.glossary_id)).map((m) => m.glossary_id);
      ids = [...ids, ...metas];
    }
    return ids;
  }, []);
  if (_isNotEmpty(metadata) && _isNotEmpty(glossaryIDs)) {
    return metadata.filter((data) => glossaryIDs.includes(data.id));
  }
  return [];
};

export const validationMessages: Record<string, string> = {
  strain: 'Species is required when a Strain is selected, otherwise it should be left blank.',
  sex: 'Sex is required.',
  dob: 'Required and must not be a future date.',
  name: 'Animal name is a required field.',
  population: `No. of animals must be a valid number, of max ${MAX_CAGE_POPULATION}.`,
  numeric: 'Please enter a number for numeric metadata.',
  all: 'Please ensure you provide an Animal name, Sex and DOB for all animals.',
};

/**
 * Builds a lookup map from species/strain names to strain IDs, returning a resolver function.
 * Returns null when species or strain is empty/cleared.
 */
export const createStrainResolver = (
  species: Array<AnimalSpecies>
): ((speciesName: string | undefined, strainName: string | undefined) => number | null) => {
  const lookup = new Map<string, number>(
    species.flatMap((s) => s.strains.map((st) => [`${s.name}::${st.name}`, Number(st.id)]))
  );
  return (speciesName, strainName) => {
    if (!_notNil(speciesName) || speciesName === '' || !_notNil(strainName) || strainName === '') {
      return null;
    }
    return lookup.get(`${speciesName}::${strainName}`) ?? null;
  };
};

export const filterOptionsFromApi = (name: string) => name === 'Study';

interface AnimalSettingsResult {
  settings: SpreadsheetSettings;
  metadata?: Array<MetadataField>;
}

/**
 * Metadata glossary fields that must always be a column because the team marked them required.
 * Retired (inactive) fields are skipped so an old requirement cannot block a save.
 */
export const getRequiredMetaColumns = (metadata: Array<MetadataField> = []): Array<MetadataField> =>
  metadata.reduce<Array<MetadataField>>((acc, field) => {
    if ((field.required ?? false) && (field.active ?? false)) {
      acc.push(field);
    }
    return acc;
  }, []);

export const addMetaColumns = (
  settings: SpreadsheetSettings,
  metaColumns: Array<MetadataField>,
  metadata: Array<MetadataField>
): AnimalSettingsResult => {
  const colHeaders = [...((settings.colHeaders as Array<string>) ?? [])];
  const columns = [...(settings.columns ?? [])];

  // Callers re-run with the same metaColumns on step revisits and refreshed species data.
  const presentMetaIDs = new Set(
    columns.reduce<Array<ID>>((acc, column) => {
      if (_notNil(column.metaID)) {
        acc.push(column.metaID);
      }
      return acc;
    }, [])
  );

  metaColumns.forEach((columnMetadata) => {
    if (presentMetaIDs.has(columnMetadata.id)) {
      return;
    }
    const { columnHeader, columnSettings } = defaultMetadataToColumnSettingMapper(columnMetadata);
    colHeaders.push(columnMetadata.required === true ? requiredAsteriskColumnHeader(columnHeader) : columnHeader);
    columns.push(columnSettings as CustomColumnSettings);
    presentMetaIDs.add(columnMetadata.id);
  });

  const newColIDs = metaColumns.map((m) => m.id);
  const newMetadata = metadata.reduce<Array<MetadataField>>((acc, item) => {
    if (!newColIDs.includes(item.id)) {
      acc.push(item);
    }
    return acc;
  }, []);

  return {
    settings: { ...settings, columns, colHeaders },
    metadata: newMetadata,
  };
};

export const initAnimalSettings = (
  species: Array<AnimalSpecies> = [],
  creation: boolean,
  removeRow: boolean,
  metaColumns?: Array<MetadataField>,
  metadata?: Array<MetadataField>,
  existingSettings?: SpreadsheetSettings,
  entityFieldsWithRequiredPair?: EntityFieldRequiredMap,
  includeUnknown: boolean = false
): AnimalSettingsResult => {
  const dobRequired = entityFieldsWithRequiredPair?.dob ?? true;
  // Strain has no flag of its own; the backend keeps it in lock-step with Species.
  const speciesRequired = entityFieldsWithRequiredPair?.species ?? false;

  const dobValidater = (value: string, callback: (valid: boolean) => void) => {
    validateDob(dobRequired, value, callback);
  };

  const dobValidationErrorMessage = (): string =>
    dobRequired
      ? 'Required in YYYY-MM-DD and must not be a future date.'
      : 'Must be in YYYY-MM-DD format and not a future date if provided.';

  const nameValidator = (value: string, callback: (valid: boolean) => void) => {
    if (!_notNil(value) || _isEmptyString(value ?? '')) {
      callback(false);
    } else if (value.length > 40) {
      callback(false);
    } else {
      callback(true);
    }
  };

  const speciesValidator = (value: string, callback: (valid: boolean) => void) => {
    if (_isNotEmpty(value)) {
      return callback(species.some((s) => s.name === value));
    }
    return callback(!speciesRequired);
  };

  const speciesValidationErrorMessage = (value: string): string =>
    _isBlank(value) ? 'Species is required.' : 'Is not a valid option';

  const strainValidationErrorMessage = (value: string): string =>
    speciesRequired
      ? _isBlank(value)
        ? 'Strain is required.'
        : 'Strain must be a valid subtype of specified Species value'
      : 'Strain is required when a Species is selected, otherwise it should be left blank.';

  const speciesColumnHeader = speciesRequired ? requiredAsteriskColumnHeader('Species') : 'Species';
  const strainColumnHeader = speciesRequired ? requiredAsteriskColumnHeader('Strain') : 'Strain';
  // No schema flag for these two: their validators reject an empty cell in every flow.
  const nameColumnHeader = requiredAsteriskColumnHeader('Animal Name');
  const sexColumnHeader = requiredAsteriskColumnHeader('Sex');
  const dobColumnHeader = dobRequired ? requiredAsteriskColumnHeader('DOB') : 'DOB';

  /**
   * Strain validator: uses `this` context (CellProperties) to read species from the same row.
   * - Species selected + strain empty -> INVALID
   * - Species selected + strain filled -> check strain belongs to that species
   * - Species empty + strain empty -> VALID, unless Species is required
   * - Species empty + strain filled -> INVALID (strain without species makes no sense)
   */
  function strainValidator(this: unknown, value: string, callback: (valid: boolean) => void) {
    const hot = (this as { instance?: Handsontable.Core }).instance;
    const row = (this as { row?: number }).row;

    if (!_notNil(hot) || !_notNil(row)) {
      return callback(true);
    }

    const speciesCol = hot.propToCol('species');
    const selectedSpecies = hot.getDataAtCell(row, speciesCol as number) as string;
    const hasSpecies = _notNil(selectedSpecies) && _isNotEmpty(selectedSpecies);
    const hasStrain = _notNil(value) && _isNotEmpty(value);

    if (!hasSpecies && !hasStrain) {
      return callback(!speciesRequired);
    }

    if (!hasSpecies && hasStrain) {
      return callback(false);
    }

    if (hasSpecies && !hasStrain) {
      return callback(false);
    }

    // Both species and strain are filled -- check strain belongs to the selected species
    const validStrains = species.reduce<Array<string>>((acc, sp) => {
      if (sp.name === selectedSpecies && _notNil(sp.strains) && _isNotEmpty(sp.strains)) {
        sp.strains.forEach((strain: AnimalStrain) => {
          acc.push(strain.name);
        });
      }
      return acc;
    }, []);

    return callback(validStrains.includes(value));
  }

  /**
   * Dynamic strain dropdown source: reads the Species selected in the same row
   * and returns the strains that belong to it. Closes over the `species` argument
   * passed on this invocation, so it must be re-assigned whenever `initAnimalSettings`
   * is called with refreshed species data (see the `existingSettings` branch below).
   * `this` is the CellProperties object with `instance` (Handsontable.Core) and `row`.
   */
  function strainSource(this: unknown, _query: string, process: (result: Array<string>) => void) {
    const hot = (this as { instance?: Handsontable.Core }).instance;
    const row = (this as { row?: number }).row;
    if (_notNil(hot) && _notNil(row)) {
      const speciesCol = hot.propToCol('species');
      const selectedSpecies = hot.getDataAtCell(row, speciesCol as number) as string;
      const strains = species.reduce<Array<string>>((acc, sp) => {
        if (sp.name === selectedSpecies && _notNil(sp.strains) && _isNotEmpty(sp.strains)) {
          sp.strains.forEach((strain: AnimalStrain) => {
            acc.push(strain.name);
          });
        }
        return acc;
      }, []);
      process(strains);
    } else {
      process([]);
    }
  }

  const hiddenColumns = creation ? [0, 1, 3] : [0, 2, 3];
  let settings: SpreadsheetSettings = existingSettings ?? {
    columns: [
      { data: 'id', type: 'numeric', width: 150, readOnly: true },
      { data: 'cage_catalog', type: 'text', width: 150, readOnly: true },
      { data: 'cage_name', type: 'text', width: 150, readOnly: true },
      { data: 'catalog', type: 'text', width: 150, readOnly: true },
      {
        data: 'name',
        type: 'text',
        width: 150,
        validator: nameValidator,
        validationErrorMessage: (value: unknown) => {
          const strValue = typeof value === 'string' ? value : '';
          if (!_notNil(strValue) || _isEmptyString(strValue ?? '')) {
            return 'Required Field';
          }
          if (strValue.length > 40) {
            return 'Must have a max length of 40 characters';
          }
          return 'Required Field';
        },
      },
      {
        data: 'sex',
        type: 'dropdown',
        source: getSexDropdownSource(includeUnknown),
        width: 150,
        validator: createSexValidator(includeUnknown),
      },
      {
        data: 'dob',
        type: DateEditorWrapper.type,
        dateFormat: DateInputUtils.hotDatePattern,
        datePickerConfig: {
          maxDate: new Date(),
        },
        width: 150,
        validator: dobValidater,
        validationErrorMessage: dobValidationErrorMessage,
      },
      {
        data: 'species',
        type: 'dropdown',
        width: 150,
        source: species.map((s) => s.name),
        validator: speciesValidator,
        validationErrorMessage: speciesValidationErrorMessage,
        linkedValidationColumns: ['strain'],
      },
      {
        data: 'strain',
        type: 'dropdown',
        width: 150,
        strict: true,
        validator: strainValidator,
        source: strainSource,
        validationErrorMessage: strainValidationErrorMessage,
      },
    ] as Array<CustomColumnSettings>,
    hiddenColumns: { columns: hiddenColumns },
    colHeaders: [
      'ID',
      'Cage No.',
      'Cage Name',
      'Animal No.',
      nameColumnHeader,
      sexColumnHeader,
      dobColumnHeader,
      speciesColumnHeader,
      strainColumnHeader,
    ],
    columnSorting: false,
    className: 'htMiddle',
    manualColumnResize: true,
    rowHeights: 50,
    height: 500,
    rowHeaders: true,
  };

  if (_notNil(existingSettings) && _notNil(settings.columns)) {
    const speciesColIndex = settings.columns.findIndex((col) => col.data === 'species');
    const speciesCol = settings.columns[speciesColIndex];
    if (_notNil(speciesCol)) {
      speciesCol.source = species.map((s) => s.name);
      speciesCol.validator = speciesValidator;
      speciesCol.validationErrorMessage = speciesValidationErrorMessage;
    }
    // Re-bind the strain column's dynamic source/validator so they close over the
    // refreshed `species` data. Without this, a reused `existingSettings` keeps the
    // closures created with the initial (empty) species list, leaving the Strain
    // dropdown permanently empty after a Species is selected.
    const strainColIndex = settings.columns.findIndex((col) => col.data === 'strain');
    const strainCol = settings.columns[strainColIndex];
    if (_notNil(strainCol)) {
      strainCol.source = strainSource;
      strainCol.validator = strainValidator;
      strainCol.validationErrorMessage = strainValidationErrorMessage;
    }

    // The first build ran before the schema resolved, so re-bind DOB to the loaded flag.
    const dobColIndex = settings.columns.findIndex((col) => col.data === 'dob');
    const dobCol = settings.columns[dobColIndex];
    if (_notNil(dobCol)) {
      dobCol.validator = dobValidater;
      dobCol.validationErrorMessage = dobValidationErrorMessage;
    }

    // Only the schema-driven headers can go stale; Name and Sex are always required.
    // Written to a copy because `existingSettings` is often reducer state that the grid still holds.
    if (Array.isArray(settings.colHeaders)) {
      const colHeaders = [...settings.colHeaders];
      if (speciesColIndex !== -1) {
        colHeaders[speciesColIndex] = speciesColumnHeader;
      }
      if (strainColIndex !== -1) {
        colHeaders[strainColIndex] = strainColumnHeader;
      }
      if (dobColIndex !== -1) {
        colHeaders[dobColIndex] = dobColumnHeader;
      }
      settings = { ...settings, colHeaders };
    }
  }

  if (_isNotEmpty(metaColumns) && _notNil(metadata)) {
    return addMetaColumns(settings, metaColumns, metadata);
  }

  return { settings };
};

// ─── Initial data builder ────────────────────────────────────────

/**
 *  version of returnInitialMetaValues — converts API metadata to flat key-value map
 * using the column data encoding.
 */
const returnInitialMetaValues = (metadata: Array<MetadataFieldWithValue>): Record<string, MetadataFieldValueTypes> =>
  metadata.reduce<Record<string, MetadataFieldValueTypes>>((acc, meta) => {
    const title = formatColumnSettingsDataValue(meta.title);
    if (Array.isArray(meta.value)) {
      acc[title] = meta.value.join(',');
    } else {
      acc[title] = meta.value;
    }
    return acc;
  }, {});

interface BulkAnimalRow {
  id: ID;
  catalog: string;
  name: string;
  dob: string | null;
  cage_catalog: string;
  cage_name: string;
  sex?: string;
  species?: string;
  strain?: string;
  meta: Record<string, MetadataFieldValueTypes>;
}

export const initialBulkAnimalData = (
  animalData: Array<Animal>,
  speciesObj: Array<AnimalSpecies>
): Array<BulkAnimalRow> =>
  animalData.reduce<Array<BulkAnimalRow>>((acc, animalItem) => {
    const { id, catalog, name, dob, sex, strain: animalStrain, metadata: animalMetadata } = animalItem;

    // Resolve cage info — handle both nested and flat structures
    const rawAnimal = animalItem as unknown as Record<string, unknown>;
    const collection = rawAnimal.collection as { data?: { catalog: string; name: string } } | undefined;
    const cage = rawAnimal.cage as { catalog: string; name: string } | undefined;
    const innerCollection = collection?.data ?? cage;

    const innerMeta = ((rawAnimal.meta as { data?: Array<MetadataFieldWithValue> })?.data ?? animalMetadata) as
      | Array<MetadataFieldWithValue>
      | undefined;

    const row: BulkAnimalRow = {
      id,
      catalog: catalog ?? '',
      name: name ?? '',
      dob,
      cage_catalog: innerCollection?.catalog ?? '',
      cage_name: innerCollection?.name ?? '',
      meta: {},
    };

    if (_notNil(innerMeta) && _isNotEmpty(innerMeta)) {
      row.meta = returnInitialMetaValues(innerMeta);
    }

    if (_notNil(sex)) {
      row.sex = AnimalSexMap[sex as AnimalSex];
    }

    const strainId = (rawAnimal as { strain_id?: number }).strain_id;
    if (_notNil(strainId)) {
      speciesObj.forEach((sp) => {
        sp.strains.forEach((s: AnimalStrain) => {
          if (s.id === strainId) {
            row.species = sp.name;
            row.strain = s.name;
          }
        });
      });
    } else if (_notNil(animalStrain)) {
      const strainObj = animalStrain as unknown as { species: { name: string }; name: string };
      row.species = strainObj.species.name;
      row.strain = strainObj.name;
    }

    acc.push(row);
    return acc;
  }, []);

// ─── Add animals initial data ────────────────────────────────────

export interface AddAnimalRow {
  id: number;
  cage_name: string;
  cage_catalog: string;
  catalog: string;
  name: string;
  sex?: string;
  dob?: string | null;
  species?: string;
  strain?: string;
  meta: Record<string, MetadataFieldValueTypes>;
}

export const initialAddAnimalData = ({ name, catalog, counts }: Cage): Array<AddAnimalRow> => [
  {
    id: 1,
    cage_name: name,
    cage_catalog: catalog ?? '',
    catalog: '',
    name: `Animal ${(counts?.animals ?? 0) + 1}`,
    meta: {},
  },
];

// ─── Species/Strain dropdown helper ──────────────────────────────

export const updateStrainSelectDropdown = (
  hot: Handsontable.Core,
  species: Array<AnimalSpecies>,
  row: number,
  selectedSpecies: string
): void => {
  const col = hot.propToCol('strain');

  const strains = species.reduce<Array<string>>((acc, sp) => {
    if (sp.name === selectedSpecies && _notNil(sp.strains) && _isNotEmpty(sp.strains)) {
      sp.strains.forEach((strain: AnimalStrain) => {
        acc.push(strain.name);
      });
    }
    return acc;
  }, []);

  hot.setCellMeta(row, col as number, 'source', strains);
  hot.setCellMeta(row, col as number, 'allowEmpty', !_notNil(selectedSpecies) || _isEmptyString(selectedSpecies));
};

// ─── Cage settings & data ─────────────────────────────────

interface CageSettingsResult {
  settings: SpreadsheetSettings;
  metadata?: Array<MetadataField>;
}

const cageNameValidator = (value: unknown, callback: (valid: boolean) => void): void => {
  const strValue = typeof value === 'string' ? value : '';
  callback(!_isEmptyString(strValue) && strValue.length <= 50);
};

const populationValidator = (value: unknown, callback: (valid: boolean) => void): void => {
  const num = Number(value);
  callback(
    !isNaN(num) && value !== null && value !== undefined && value !== '' && num >= 0 && num <= MAX_CAGE_POPULATION
  );
};

export const initBulkCageSettings = (
  metaColumns: Array<MetadataField>,
  metadata: Array<MetadataField>
): CageSettingsResult => {
  const settings: SpreadsheetSettings = {
    columns: [
      { data: 'catalog', width: 150, readOnly: true },
      {
        data: 'name',
        type: 'text',
        width: 150,
        validator: cageNameValidator,
        validationErrorMessage: (value: unknown) => `${value ?? 'Value'} name must have a maximum of 50 characters`,
        linkedValidationColumns: ['population'],
      },
      {
        data: 'population',
        type: 'numeric',
        width: 150,
        readOnly: true,
        validator: populationValidator,
        validationErrorMessage: (value: unknown) =>
          `${value ?? 'Value'} must be a number with a maximum of ${MAX_CAGE_POPULATION}`,
        linkedValidationColumns: ['name'],
      },
    ] as Array<CustomColumnSettings>,
    colHeaders: ['No', 'Cage Name', 'Population'],
    hiddenColumns: { columns: [0] },
    columnSorting: false,
    contextMenu: false,
    className: 'htMiddle',
    manualColumnResize: true,
    rowHeights: 50,
    height: 500,
    rowHeaders: true,
  };

  if (_isNotEmpty(metaColumns)) {
    return addMetaColumns(settings, metaColumns, metadata);
  }

  return { settings };
};

interface BulkCageRow {
  id: ID;
  catalog: string;
  name: string;
  population: number;
  meta: Record<string, MetadataFieldValueTypes>;
}

export const initialBulkCageData = (selectedCages: Array<unknown>): Array<BulkCageRow> =>
  (selectedCages ?? []).reduce<Array<BulkCageRow>>((acc, rawCage) => {
    const cage = rawCage as {
      id: ID;
      name: string;
      catalog: string;
      counts: { animals: number };
      meta: { data?: Array<MetadataFieldWithValue> };
    };

    const row: BulkCageRow = {
      id: cage.id,
      catalog: cage.catalog ?? '',
      name: cage.name ?? '',
      population: cage.counts?.animals ?? 0,
      meta: {},
    };

    const innerMeta = cage.meta?.data;
    if (_notNil(innerMeta) && _isNotEmpty(innerMeta)) {
      row.meta = returnInitialMetaValues(innerMeta);
    }

    acc.push(row);
    return acc;
  }, []);
